import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { UserService } from 'src/app/core/services/user.service';
import { ActionFlowService } from 'src/app/core/services/action-flow.service';
import { clearQuestionnairesSkippedInSession } from 'src/app/shared/utils/elective';

/** Decodes the `email` claim from a JWT payload without verifying its signature. */
function decodeJwtEmail(jwt: string | null): string | null {
  if (!jwt) return null;
  try {
    const payload = jwt.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json).email ?? null;
  } catch {
    return null;
  }
}

@Component({
  selector: 'app-one-time-link',
  templateUrl: './one-time-link.component.html',
  styleUrls: ['./one-time-link.component.scss'],
})
export class OneTimeLinkComponent {

  // Token de login mágico
  token: string | null = null;

  // Tokens LP
  tokenLP: string | null = null;
  refreshTokenLP: string | null = null;

  // Token de email (access token, refresh opcional)
  emailToken: string | null = null;
  emailRefreshToken: string | null = null;

  showMagicLinkButton = false;
  loading = false;

  verificationMessage: string = '';
  constructor(
    private route: ActivatedRoute,
    private as: UserService,
    private router: Router,
    private toastService: ToastService,
    private actionFlowService: ActionFlowService
  ) {}

  async ngOnInit(): Promise<void> {
    console.log('[OTL] Loaded OTL Component');
    this.token = this.route.snapshot.queryParamMap.get('token');

    this.tokenLP = this.route.snapshot.queryParamMap.get('tokenLP');
    this.refreshTokenLP = this.route.snapshot.queryParamMap.get('refreshTokenLP');

    // Token de email (access token sem refresh token)
    this.emailToken = this.route.snapshot.queryParamMap.get('emailToken');
    this.emailRefreshToken = this.route.snapshot.queryParamMap.get('emailRefreshToken');

    // Agent document upload params
    const questionnaireId = this.route.snapshot.queryParamMap.get('questionnaireId');
    const skipGuide = this.route.snapshot.queryParamMap.get('skipGuide');
    // CPK-4846: descarta o alvo de uma visita OTL anterior (ex: upload de arquivos
    // abandonado) — senão o guia de uma sessão sem questionnaireId, como a do
    // Simple Questionnaire, pularia para aquele questionário.
    localStorage.removeItem('agentUploadQuestionnaireId');
    if (questionnaireId && skipGuide === 'true') {
      localStorage.setItem('agentUploadQuestionnaireId', questionnaireId);
    }

    // CPK-4743: sessão de "Simple Questionnaire" (F2). Descarta sempre o resíduo
    // de uma visita OTL anterior antes de decidir se seta de novo — do contrário
    // uma sessão simples abandonada vazaria a flag para uma sessão normal futura
    // no mesmo browser (mesmo risco do CPK-3909, ver postLoginRedirect abaixo).
    const simpleQuestionnaire = this.route.snapshot.queryParamMap.get('simpleQuestionnaire');
    localStorage.removeItem('simpleQuestionnaireSession');
    // CPK-4878: questionários que o agente deixou OPEN com puláveis vazias valem
    // como feitos só na sessão em que foram pulados — nunca na próxima.
    clearQuestionnairesSkippedInSession();
    if (simpleQuestionnaire === 'true') {
      localStorage.setItem('simpleQuestionnaireSession', 'true');
    }

    // Redirect explícito passado pela LP (ex: /client/claim/<id>/guide)
    // Tem prioridade sobre o postLoginRedirect do localStorage.
    // Descarta qualquer valor residual de uma visita OTL anterior não
    // relacionada (ex: outro caso/claim) antes de aplicar o novo, para que
    // uma sessão abandonada não vaze o destino para um cadastro futuro em
    // outro caso (CPK-3909).
    const redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    const legacyRedirect = this.route.snapshot.queryParamMap.get('redirect');
    const postLoginRedirect = redirectTo || legacyRedirect;
    localStorage.removeItem('postLoginRedirect');
    localStorage.removeItem('postLoginRedirectBypassGuide');
    if (postLoginRedirect) {
      localStorage.setItem('postLoginRedirect', postLoginRedirect);

      if (postLoginRedirect.startsWith('/client/cases/')) {
        localStorage.setItem('postLoginRedirectBypassGuide', postLoginRedirect);
      }
    }

    console.log('[OTL] Retrieved tokens from query params', {
      token: this.token,
      tokenLP: this.tokenLP,
      refreshTokenLP: this.refreshTokenLP,
      emailToken: this.emailToken,
      emailRefreshToken: this.emailRefreshToken,
      questionnaireId,
      skipGuide,
      simpleQuestionnaire,
      redirectTo,
      legacyRedirect,
      postLoginRedirect,
    });

    if (this.emailToken) {
      const success = await this.handleEmailToken();
      if (success) return;
    }

    if (this.tokenLP && this.refreshTokenLP) {
      await this.handleLPToken();
    }

    if (this.token) {
      await this.handleMagicLinkToken();
    }
  }

  private async handleEmailToken(): Promise<boolean> {
    console.log('[OTL] Processing email token', { hasRefreshToken: !!this.emailRefreshToken });
    const req = await this.as.createSessionWithAccessTokenOnly(
      this.emailToken!,
      this.emailRefreshToken || undefined
    );

    if (!req.error && req.session) {
      console.log('[OTL] Session created successfully with email token, waiting for user...');
      const user = await this.as.waitForCurrentUser();
      console.log('[OTL] waitForCurrentUser result:', {
        id: user?.id,
        auth_id: user?.auth_id,
        display_name: user?.display_name
      });

      await this.redirectUser();
      return true;
    } else {
      console.error('[OTL] Email token session creation failed:', req.error);
      this.toastService.error(req.error?.message || 'Invalid or expired token');
      return false;
    }
  }

  private async handleLPToken(): Promise<void> {
    console.log('[OTL] Processing LP tokens');
    // ✅ CPK-4172: único caminho disparado exclusivamente por login originado
    // de uma landing page (tokenLP/refreshTokenLP) — usado pelo nav lock do
    // client portal até o usuário assinar algum legal pack.
    localStorage.setItem('cpCameFromLp', 'true');
    const req: any = await this.as.createSessionWithRedirect(this.tokenLP!, this.refreshTokenLP!);

    if (!req.error) {
      console.log('[OTL] Session created successfully, waiting for user...');
      // Aguarda o usuário ser carregado para evitar race condition na home
      const user = await this.as.waitForCurrentUser();
      console.log('[OTL] waitForCurrentUser result:', {
        id: user?.id,
        auth_id: user?.auth_id,
        display_name: user?.display_name
      });

      await this.redirectUser();
    } else {
      console.error('[OTL] Session creation failed:', req.error);
      await this.handleExpiredLPToken();
    }
  }

  /**
   * O tokenLP é um access_token Supabase real, emitido pelas automations (n8n) com validade
   * estendida (CPK-4180). Quando expira, o payload continua decodificável (JWT sem verificação
   * de assinatura), o que permite recuperar o email do titular para disparar um novo magic link.
   */
  private async handleExpiredLPToken(): Promise<void> {
    const email = decodeJwtEmail(this.tokenLP);

    if (!email) {
      this.toastService.error('Invalid or expired token');
      return;
    }

    const phone = await this.as.resendExpiredLink(email).catch(() => null);

    this.router.navigate(['/auth/link-expired'], {
      queryParams: { email },
      state: { phone },
    });
  }

  async handleMagicLinkToken(): Promise<void> {
    this.loading = true;
    const a = await this.as.signInWithMagicLink(this.token!);

    if (!a.error) {
      // Aguarda o usuário ser carregado para evitar race condition na home
      await this.as.waitForCurrentUser();

      await this.redirectUser();
    } else {
      this.loading = false;
      this.toastService.error(a.error.message);
      this.router.navigateByUrl('/auth/login');
    }
  }

  private async redirectUser(): Promise<void> {
    const redirectPath = localStorage.getItem('postLoginRedirect');
    localStorage.removeItem('postLoginRedirect');

    const user = await this.as.getCurrentUser();
    const resolvedPath = await this.actionFlowService.resolvePendingActionRedirect(
      redirectPath,
      user?.id
    );

    this.router.navigateByUrl(resolvedPath);
  }
}
